import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_URL } from '@akademiasaas/shared';
import LanguageMenu from '~/components/shell/LanguageMenu';
import { normalizeLanguage } from '~/components/shell/language';
import logo from '~/assets/icons/logo-black.png';
import SilkBackground from './silk/SilkBackground';
import styles from './AuthLayout.module.scss';

export const LEGAL_LINKS = {
  terms: 'https://docuwatcher.com/terms-and-conditions/',
  privacy: 'https://docuwatcher.com/privacy-policy/',
  contact: 'https://docuwatcher.com/kontakt/',
} as const;

/**
 * Ramka ekranów /auth (makieta `d2-logowanie`): biała strona z liniami pomocniczymi, logo na środku
 * nagłówka (poza torem wstęg), język po prawej, jedwabne wstęgi za kartą, stopka z linkami prawnymi.
 * Montowana raz dla wszystkich ekranów — przejście między nimi zmienia tylko treść karty.
 */
const AuthLayout: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation('auth');
  const [languageOpen, setLanguageOpen] = useState(false);

  return (
    <div className={styles.page}>
      <div className={styles.guides} aria-hidden="true" />
      <SilkBackground />
      <header className={styles.header}>
        <a className={styles.logo} href={APP_URL} target="_blank" rel="noopener noreferrer">
          <img src={logo} alt={t('auth:screens.shell.home')} />
        </a>
        <div className={styles.language}>
          <LanguageMenu
            persistent
            language={normalizeLanguage(i18n.language)}
            onLanguageChange={(language) => i18n.changeLanguage(language)}
            open={languageOpen}
            onOpenChange={setLanguageOpen}
          />
        </div>
      </header>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        <span>{t('auth:screens.shell.copyright', { year: new Date().getFullYear() })}</span>
        <nav aria-label={t('auth:screens.shell.legal')} className={styles.legal}>
          <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
            {t('auth:screens.shell.terms')}
          </a>
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
            {t('auth:screens.shell.privacy')}
          </a>
          <a href={LEGAL_LINKS.contact} target="_blank" rel="noopener noreferrer">
            {t('auth:screens.shell.contact')}
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default AuthLayout;
